import { ChakraProvider } from '@chakra-ui/react';
import { newStore, store } from 'modules/Editor/store';
import { StrictMode } from 'react';
import { Root, createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { theme } from '../../theme/theme';
import { BrandTemplatePreviewContainer } from './BrandTemplatePreview';
import { addLatestBrandTemplate } from './BrandTemplatePreview.helpers';
import { BrandTemplateData } from './BrandTemplatePreview.type';
import { BrandTemplatePreviewProvider } from './useBrandTemplatePreview';

interface MultipleStore {
  id: string;
  store: typeof store;
}

declare global {
  interface Window {
    __editor2_render: (element: HTMLElement, brandTemplateData?: BrandTemplateData) => Promise<void>;
    __editor2_multiple_stores: MultipleStore[];
  }
}

let lastRootElement: Map<string, Root> = new Map();
let root: Root;

const renderToElement = async (element: HTMLElement, brandTemplateData?: BrandTemplateData) => {
  if (element) {
    // From AI-tools generation, we don't directly create the design anymore but rather create the design
    // once users access the /design/create route. In that case, we don't have the real templateId in onboarding,
    // so we can just pass the real infographState
    const {
      templateId,
      colors,
      fontConfig,
      targetHeight,
      prefix,
      infographState,
      logos,
      shouldCache = true,
    } = brandTemplateData || {};

    if (!templateId) {
      console.error('Template ID is required');
      return;
    }

    const templateBrandData = {
      templateId,
      colors,
      fontConfig,
      targetHeight,
      infographState,
      logos,
      shouldCache,
    };
    /**
     * Note: we use in memory storage to store the preview data instead of refetching from the server.
     */
    addLatestBrandTemplate(templateBrandData);

    const prefixTemplateId = prefix ? `${prefix}-${templateId}` : templateId;

    const store = newStore(); // Create a new store for each template
    const existingStoreIndex = window.__editor2_multiple_stores.findIndex((obj) => obj.id === prefixTemplateId);
    const newTemplateStore = {
      id: prefixTemplateId,
      store,
    };
    if (existingStoreIndex !== -1) {
      window.__editor2_multiple_stores[existingStoreIndex] = newTemplateStore;
    } else {
      window.__editor2_multiple_stores.push(newTemplateStore);
    }

    try {
      if (lastRootElement.get(prefixTemplateId)) {
        lastRootElement.get(prefixTemplateId)?.unmount();
        lastRootElement.delete(prefixTemplateId);
      }
      if (!lastRootElement.get(prefixTemplateId)) {
        root = createRoot(element);
        lastRootElement.set(prefixTemplateId, root);
      }

      // docuseal requires a router, without it cause the app to crash
      const router = createBrowserRouter([
        {
          path: '*',
          element: (
            <StrictMode>
              <ChakraProvider theme={theme}>
                <Provider store={store}>
                  <BrandTemplatePreviewProvider templateBrandData={templateBrandData}>
                    <BrandTemplatePreviewContainer customStore={store} targetHeight={targetHeight} />
                  </BrandTemplatePreviewProvider>
                </Provider>
              </ChakraProvider>
            </StrictMode>
          ),
        },
      ]);

      lastRootElement.get(prefixTemplateId)?.render(<RouterProvider router={router} />);
    } catch (error) {
      console.error('Error rendering the app', error);
    }
  }
};

export const initIntegration = () => {
  window.__editor2_render = renderToElement;
  window.__editor2_multiple_stores = [];
};
